import { useState, useCallback, useEffect, useContext } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { connect, currentPageParams } from 'scrivito'

import { FILTERS_DROPDOWN_WIDTH } from './constants'
import { FiltersDropdown } from './FiltersDropdown'
import { FilterSheetErrorBoundary } from './FilterSheetErrorBoundary'
import { GroupFilter } from './GroupFilter'
import { ListFilter } from './ListFilter'
import { MobileFilterContext } from './MobileFilterContext'
import { ObjectFilter } from './ObjectFilter'
import { StatusFilter } from './StatusFilter'
import { TypeFilter } from './TypeFilter'
import { Icon } from '@/Atomic/Atoms/Icon'
import { Button } from '@/Atomic/Molecules/Button'
import { DateRangeDropdown } from '@/Atomic/Molecules/DateRangeDropdown'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'
import { AdvancedSearchModal } from '@/Atomic/Widgets/AdvancedSearchModal'
import { FormContext, FormContextProvider } from '@/Components/FormContext'
import { FeatureFlags } from '@/Data/const'
import { useActiveFilter } from '@/hooks/useActiveFilter'
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter'
import { useEntitySearch } from '@/hooks/useEntitySearch'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useSmartPosition } from '@/hooks/useSmartPosition'
import { ensureString } from '@/utils/ensureString'
import { useFeatureFlagAccess } from '@/utils/featureFlagConfig'
import {
  getActiveAttributeFilterCount,
  paramsToFilters,
  readCommittedFilters,
  writeFilters,
} from '@/utils/filterParams'

import './EntitySearchHeader.scss'

const PHONE_MAX_WIDTH = 766
const TABLET_MAX_WIDTH = 1199
const SMALL_DESKTOP_MAX_WIDTH = 1439

interface StatusFilterConfig {
  dataClassName?: string
  filterKey?: string
}

interface DateRangeFilterConfig {
  fieldPrefix?: string
}

interface EntitySearchHeaderProps {
  dataClassName: string
  showStatusFilter?: boolean
  statusFilterConfig?: StatusFilterConfig
  showTypeFilter?: boolean
  showGroupFilter?: boolean
  showDateRangeFilter?: boolean
  dateRangeFilterConfig?: DateRangeFilterConfig
  showObjectFilter?: boolean
  children?: React.ReactNode
}

export const EntitySearchHeader = connect(function EntitySearchHeader({
  dataClassName,
  showStatusFilter,
  statusFilterConfig,
  showTypeFilter,
  showGroupFilter,
  showDateRangeFilter,
  dateRangeFilterConfig,
  showObjectFilter,
  children,
}: EntitySearchHeaderProps) {
  const { t } = useTranslation()
  const { searchRef, onKeyDown, onChange, searchValue } = useEntitySearch()
  const dateRange = useDateRangeFilter(dateRangeFilterConfig?.fieldPrefix)

  const params = currentPageParams()
  const labels = ensureString(params['labels'] ?? null)

  // Read committed attribute filters for the "has active filters" gate
  const attributeFilters = paramsToFilters(params)

  const { isFeatureFlagEnabled } = useFeatureFlagAccess()

  // The advanced filter is compiled from the URL, not held in state. ListFilter
  // and AdvancedSearchModal are connect()-wrapped and call useActiveFilter
  // themselves, so nothing here is threaded down to them.
  const { activeSource, applyDraft, clearAdvancedFilter, clearAllFilters } =
    useActiveFilter(dataClassName)

  // Advanced Search Modal state. `seeded` is modal *mode*, not filter data: the
  // "Advanced filter" button opens a blank builder for a new filter, while Edit
  // opens it pre-filled from the URL draft.
  const [advancedSearchMode, setAdvancedSearchMode] = useState<
    'closed' | 'blank' | 'seeded'
  >('closed')

  // Responsive viewport ranges:
  // - ≥1440px: big desktop (inline filters)
  // - 1200px-1439px: small desktop (filters dropdown)
  // - 767px-1199px: tablet (inline filters in a second row)
  // - ≤766px: phone (filter sheets)
  const isAtMostSmallDesktop = useMediaQuery(
    `(max-width: ${SMALL_DESKTOP_MAX_WIDTH}px)`,
  )
  const isAtMostTablet = useMediaQuery(`(max-width: ${TABLET_MAX_WIDTH}px)`)
  const isMobile = useMediaQuery(`(max-width: ${PHONE_MAX_WIDTH}px)`)

  const filterMenuGroup = `filters-${dataClassName}`

  const {
    buttonRef: filtersButtonRef,
    menuIsOpen: isFiltersDropdownOpen,
    setMenuIsOpen: setIsFiltersDropdownOpen,
  } = useIconMenu({
    group: filterMenuGroup,
  })

  // Mobile filters popup state
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  // Mobile advanced filters popup state
  const [isMobileAdvancedFiltersOpen, setIsMobileAdvancedFiltersOpen] =
    useState(false)

  // When opening mobile filters, just open the sheet — FormContextProvider seeds from URL on mount
  const openMobileFilters = useCallback(() => {
    setIsMobileFiltersOpen(true)
  }, [setIsMobileFiltersOpen])

  const toggleFiltersDropdown = useCallback(() => {
    if (isFiltersDropdownOpen) {
      setIsFiltersDropdownOpen(false)
    } else {
      setIsFiltersDropdownOpen(true)
    }
  }, [isFiltersDropdownOpen, setIsFiltersDropdownOpen])

  const cancelMobileFilters = useCallback(() => {
    setIsMobileFiltersOpen(false)
  }, [setIsMobileFiltersOpen])

  // Use smart position for the filters dropdown
  const {
    panelRef: filtersDropdownRef,
    position: filtersDropdownPosition,
    hasMeasured: filtersDropdownMeasured,
  } = useSmartPosition({
    isOpen: isFiltersDropdownOpen,
    triggerRef: filtersButtonRef,
    preferHorizontal: 'left',
    preferVertical: 'bottom',
    listenToScroll: true,
  })

  // Small desktop is exactly 1200px-1439px. For LIST the filter panel is always
  // shown as a dropdown (never as inline selects), matching the Figma design.
  const shouldShowDropdown =
    (isAtMostSmallDesktop && !isAtMostTablet) || !!showObjectFilter

  // Close filters dropdown when viewport leaves the 1200px-1439px range.
  useEffect(() => {
    if (!shouldShowDropdown && isFiltersDropdownOpen) {
      setIsFiltersDropdownOpen(false)
    }
  }, [shouldShowDropdown, isFiltersDropdownOpen, setIsFiltersDropdownOpen])

  // Close mobile filters popup when viewport is no longer mobile
  // This effect synchronizes viewport state (external system) with component state
  useEffect(() => {
    if (!isMobile && isMobileFiltersOpen) {
      setIsMobileFiltersOpen(false)
    }
    if (!isMobile && isMobileAdvancedFiltersOpen) {
      setIsMobileAdvancedFiltersOpen(false)
    }
  }, [isMobile, isMobileFiltersOpen, isMobileAdvancedFiltersOpen])

  // "Advanced filter" builds a NEW filter, so it opens blank; Edit (from the
  // dropdown's 3-dot menu) opens the existing draft.
  const openAdvancedSearch = useCallback(
    (mode: 'blank' | 'seeded' = 'blank') => {
      if (isMobile) {
        setIsMobileAdvancedFiltersOpen(true)
      }
      setAdvancedSearchMode(mode)
    },
    [isMobile],
  )

  const closeAdvancedSearch = useCallback(() => {
    setAdvancedSearchMode('closed')
    setIsMobileAdvancedFiltersOpen(false)
  }, [])

  const hasActiveFilters =
    activeSource !== 'none' ||
    Object.keys(attributeFilters).length > 0 ||
    !!labels

  // Count active quick filters (not including advanced search filter). Date
  // ranges create paired .gte/.lte keys, but represent one visible filter.
  const activeQuickFiltersCount =
    getActiveAttributeFilterCount(attributeFilters) + (labels ? 1 : 0)
  const filterButtonAriaLabel =
    activeQuickFiltersCount > 0
      ? `${t('common.filter.title')} - ${activeQuickFiltersCount} ${t('common.filter.active')}`
      : t('common.filter.title')
  const mobileFiltersErrorFallback = (
    <div className="mobile-filters-content" role="alert">
      <h3 className="mobile-filters-title">{t('search.error.title')}</h3>
      <p className="mobile-filters-error-message">
        {t('search.error.subtitle')}
      </p>
      <div className="mobile-filters-actions">
        <Button variant="primary" size="sm" onClick={cancelMobileFilters}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  )
  const mobileAdvancedFiltersErrorFallback = (
    <div className="mobile-filters-content" role="alert">
      <h3 className="mobile-filters-title">{t('search.error.title')}</h3>
      <p className="mobile-filters-error-message">
        {t('search.error.subtitle')}
      </p>
      <div className="mobile-filters-actions">
        <Button variant="primary" size="sm" onClick={closeAdvancedSearch}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  )

  // Advanced Search Modal is behind feature flag
  const isAdvancedSearchModalEnabled = isFeatureFlagEnabled(
    FeatureFlags.SHOW_ADVANCED_SEARCH,
  )

  const advancedFilterClickHandler = isAdvancedSearchModalEnabled
    ? openAdvancedSearch
    : undefined

  // Check if any quick filters are enabled
  const hasQuickFilters =
    showStatusFilter ||
    showTypeFilter ||
    showGroupFilter ||
    showDateRangeFilter ||
    showObjectFilter

  return (
    <div className="dxp-search-container">
      <div className="row mb-2 dxp-search-header--advanced">
        <div className="col-auto dxp-search-header__search">
          <div className="input-group input-group-search--advanced">
            <span className="search-icon">
              <Icon icon="search" size="md" />
            </span>
            <input
              type="search"
              className="form-control"
              ref={searchRef}
              onKeyDown={onKeyDown}
              onChange={onChange}
              value={searchValue}
              placeholder={`${t('search.placeHolder')}`}
            />
          </div>
        </div>
        <div className="col-auto dxp-search-header__filters">
          <div className="filter-controls-wrapper">
            {/* Mobile view (≤766px): Show filter buttons in rows */}
            {isMobile ? (
              <>
                {hasQuickFilters && (
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={openMobileFilters}
                    buttonClassName="mobile-filter-button"
                    aria-expanded={isMobileFiltersOpen}
                    aria-haspopup="dialog"
                    aria-label={filterButtonAriaLabel}
                  >
                    <span>
                      {activeQuickFiltersCount > 0
                        ? `${t('common.filter.title')} (${activeQuickFiltersCount})`
                        : t('common.filter.title')}
                    </span>
                    <Icon icon="chevron-down" size="md" />
                  </Button>
                )}
                {isAdvancedSearchModalEnabled && (
                  <ListFilter
                    entityName={dataClassName}
                    onAdvancedFilterClick={advancedFilterClickHandler}
                  />
                )}
              </>
            ) : (
              <>
                {/* Desktop/Tablet view: Show "Filter by" label and inline/dropdown filters */}
                {hasQuickFilters && !shouldShowDropdown && (
                  <span className="filter-by-label">
                    {t('common.filterBy')}
                  </span>
                )}

                {/* Show inline filters on:
                    - Big desktop (≥1440px): default layout
                    - Tablet (767px-1199px): separate row */}
                {!shouldShowDropdown && (
                  <>
                    {showObjectFilter ? <ObjectFilter /> : null}
                    {showTypeFilter ? (
                      <TypeFilter group={filterMenuGroup} />
                    ) : null}
                    {showStatusFilter ? (
                      <StatusFilter
                        {...statusFilterConfig}
                        group={filterMenuGroup}
                      />
                    ) : null}
                    {showGroupFilter ? (
                      <GroupFilter group={filterMenuGroup} />
                    ) : null}
                    {showDateRangeFilter ? (
                      <div data-testid="date-range-filter">
                        <DateRangeDropdown
                          presets={dateRange.presets}
                          value={dateRange.value}
                          onChange={dateRange.onChange}
                          triggerClassName="filter-button"
                        />
                      </div>
                    ) : null}
                  </>
                )}

                {/* Show filters dropdown button only in small desktop range (1200px-1439px) */}
                {shouldShowDropdown && hasQuickFilters && (
                  <div
                    className="position-relative d-inline-block"
                    ref={filtersButtonRef}
                  >
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={toggleFiltersDropdown}
                      buttonClassName="filter-button"
                      aria-expanded={isFiltersDropdownOpen}
                      aria-haspopup="true"
                      aria-controls="filters-dropdown-panel"
                      aria-label={filterButtonAriaLabel}
                    >
                      <span>
                        {activeQuickFiltersCount > 0
                          ? `${t('common.filters')} (${activeQuickFiltersCount})`
                          : t('common.filters')}
                      </span>
                      <Icon icon="chevron-down" size="md" />
                    </Button>

                    {isFiltersDropdownOpen && (
                      <div
                        id="filters-dropdown-panel"
                        ref={filtersDropdownRef}
                        className={classNames(
                          'o-dropdown-panel',
                          'filter-list-panel',
                          `pos-${filtersDropdownPosition}`,
                        )}
                        style={{
                          visibility: filtersDropdownMeasured
                            ? 'visible'
                            : 'hidden',
                          width: `${FILTERS_DROPDOWN_WIDTH}px`,
                          zIndex: 1000,
                        }}
                        role="region"
                        aria-label={t('common.filter.title')}
                      >
                        <FiltersDropdown
                          showObjectFilter={showObjectFilter}
                          showStatusFilter={showStatusFilter}
                          statusFilterConfig={statusFilterConfig}
                          showTypeFilter={showTypeFilter}
                          showGroupFilter={showGroupFilter}
                          showDateRangeFilter={showDateRangeFilter}
                          dateRangeFilterConfig={dateRangeFilterConfig}
                          onClearAll={() => writeFilters({})}
                          onClose={() => setIsFiltersDropdownOpen(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
                <ListFilter
                  entityName={dataClassName}
                  onAdvancedFilterClick={advancedFilterClickHandler}
                  group={filterMenuGroup}
                />

                {hasActiveFilters ? (
                  <button
                    type="button"
                    className="clear-all-filters-btn"
                    onClick={clearAllFilters}
                  >
                    {t('common.filter.clearAll')}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className="col dxp-search-header__spacer"></div>
        <div className="col-auto dxp-search-header__actions">{children}</div>
      </div>
      {/* Desktop advanced search modal */}
      {!isMobile && isAdvancedSearchModalEnabled && (
        <AdvancedSearchModal
          isOpen={advancedSearchMode !== 'closed'}
          seeded={advancedSearchMode === 'seeded'}
          onClose={closeAdvancedSearch}
          onSearch={applyDraft}
          onClearAll={clearAdvancedFilter}
          entityName={dataClassName}
        />
      )}
      {/* Mobile filters popup */}
      {isMobile && (
        <MenuSheet
          isOpen={isMobileFiltersOpen}
          position="bottom-left"
          onBackdropClick={cancelMobileFilters}
          onMobileCloseClick={cancelMobileFilters}
          panelClassName="mobile-filters-sheet"
        >
          <FilterSheetErrorBoundary
            fallback={mobileFiltersErrorFallback}
            resetKey={isMobileFiltersOpen}
          >
            <MobileFilterContext.Provider value={{ isMobile: true }}>
              {isMobileFiltersOpen && (
                <FormContextProvider
                  initialValues={readCommittedFilters()}
                  commit={(values) => {
                    writeFilters(values)
                    setIsMobileFiltersOpen(false)
                  }}
                >
                  <MobileFiltersContent
                    showTypeFilter={showTypeFilter}
                    showStatusFilter={showStatusFilter}
                    statusFilterConfig={statusFilterConfig}
                    showGroupFilter={showGroupFilter}
                    showDateRangeFilter={showDateRangeFilter}
                    dateRangeFilterConfig={dateRangeFilterConfig}
                  />
                </FormContextProvider>
              )}
            </MobileFilterContext.Provider>
          </FilterSheetErrorBoundary>
        </MenuSheet>
      )}
      {/* Mobile advanced filters popup */}
      {isMobile && isAdvancedSearchModalEnabled && (
        <MenuSheet
          isOpen={isMobileAdvancedFiltersOpen}
          position="bottom-left"
          onBackdropClick={closeAdvancedSearch}
          onMobileCloseClick={closeAdvancedSearch}
          panelClassName="mobile-advanced-filters-sheet"
        >
          <FilterSheetErrorBoundary
            fallback={mobileAdvancedFiltersErrorFallback}
            resetKey={isMobileAdvancedFiltersOpen}
          >
            <div className="mobile-advanced-filters-content">
              <AdvancedSearchModal
                isOpen={true}
                seeded={advancedSearchMode === 'seeded'}
                onClose={closeAdvancedSearch}
                onSearch={applyDraft}
                onClearAll={clearAdvancedFilter}
                entityName={dataClassName}
                renderInline
              />
            </div>
          </FilterSheetErrorBoundary>
        </MenuSheet>
      )}
    </div>
  )
})

interface MobileFiltersContentProps {
  showTypeFilter?: boolean
  showStatusFilter?: boolean
  statusFilterConfig?: StatusFilterConfig
  showGroupFilter?: boolean
  showDateRangeFilter?: boolean
  dateRangeFilterConfig?: DateRangeFilterConfig
}

const MobileFiltersContent: React.FC<MobileFiltersContentProps> = ({
  showTypeFilter,
  showStatusFilter,
  statusFilterConfig,
  showGroupFilter,
  showDateRangeFilter,
  dateRangeFilterConfig,
}) => {
  const { t } = useTranslation()
  const ctx = useContext(FormContext)
  const dateRange = useDateRangeFilter(dateRangeFilterConfig?.fieldPrefix)

  const hasTempActiveFilters =
    ctx !== null && getActiveAttributeFilterCount(ctx.values) > 0

  const handleClearAll = () => {
    ctx?.setValues({})
  }

  const handleApply = () => {
    ctx?.apply()
  }

  return (
    <div className="mobile-filters-content">
      <h3 className="mobile-filters-title">{t('common.filters')}</h3>
      <div className="mobile-filters-list">
        {showTypeFilter && (
          <div className="mobile-filter-row">
            <TypeFilter
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="mobile-filter-label-wrapper">
                  <label className="mobile-filter-label">
                    {t('common.filter.type')}
                  </label>
                  {hasValue && (
                    <button
                      className="mobile-filter-clear-button"
                      onClick={onClear}
                      type="button"
                    >
                      {t('common.clear')}
                    </button>
                  )}
                </div>
              )}
            />
          </div>
        )}
        {showStatusFilter && (
          <div className="mobile-filter-row">
            <StatusFilter
              {...statusFilterConfig}
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="mobile-filter-label-wrapper">
                  <label className="mobile-filter-label">
                    {t('common.filter.status')}
                  </label>
                  {hasValue && (
                    <button
                      className="mobile-filter-clear-button"
                      onClick={onClear}
                      type="button"
                    >
                      {t('common.clear')}
                    </button>
                  )}
                </div>
              )}
            />
          </div>
        )}
        {showGroupFilter && (
          <div className="mobile-filter-row">
            <GroupFilter
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="mobile-filter-label-wrapper">
                  <label className="mobile-filter-label">
                    {t('common.filter.group')}
                  </label>
                  {hasValue && (
                    <button
                      className="mobile-filter-clear-button"
                      onClick={onClear}
                      type="button"
                    >
                      {t('common.clear')}
                    </button>
                  )}
                </div>
              )}
            />
          </div>
        )}
        {showDateRangeFilter && (
          <div className="mobile-filter-row">
            <div className="mobile-filter-label-wrapper">
              <label className="mobile-filter-label">
                {t('common.filter.dateRange')}
              </label>
            </div>
            <DateRangeDropdown
              presets={dateRange.presets}
              value={dateRange.value}
              onChange={dateRange.onChange}
            />
          </div>
        )}
      </div>
      <div className="mobile-filters-actions">
        <Button
          variant="outlined"
          size="sm"
          onClick={handleClearAll}
          disabled={!hasTempActiveFilters}
          aria-label={
            hasTempActiveFilters
              ? t('common.filter.clearAll')
              : `${t('common.filter.clearAll')} - ${t('common.filter.noActiveFilters')}`
          }
        >
          {t('common.filter.clearAll')}
        </Button>
        <Button variant="primary" size="sm" onClick={handleApply}>
          {t('common.apply')}
        </Button>
      </div>
    </div>
  )
}
